import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plug, BarChart3, Loader2, RefreshCw, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";
import { GoogleAdsCard } from "@/components/integrations/GoogleAdsCard";
import { SearchConsoleCard } from "@/components/integrations/SearchConsoleCard";
import { GoogleMerchantCard } from "@/components/integrations/GoogleMerchantCard";
import { EbayIntegrationCard } from "@/components/integrations/EbayIntegrationCard";
import SendGridCard from "@/components/email/SendGridCard";
import { WaConnectCard } from "@/components/whatsapp/WaConnectCard";
import { useAuth } from "@/context/AuthContext";
import { LoadError, loadErrMsg } from "@/components/integrations/LoadError";

const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };
const GA_MSG = { connected: "Google Analytics connected. Now choose a GA4 property.", denied: "Google sign-in was cancelled.", invalid_state: "The sign-in link expired. Please try again.",
  token_failed: "Google rejected the sign-in. Check the Client ID, Secret and redirect URI.", scope_missing: "Analytics read access wasn't granted. Tick the Analytics permission and try again.",
  no_refresh_token: "Google didn't return offline access. Remove TedOps from your Google account permissions and reconnect." };

function CredentialsForm({ c, onSaved }) {
  const [id, setId] = useState("");
  const [sec, setSec] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/integrations/google-oauth/credentials", { client_id: id, client_secret: sec }); setId(""); setSec(""); toast.success("Google credentials saved securely"); onSaved(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="ga-creds-section">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><KeyRound className="w-4 h-4 text-slate-400" />Google OAuth client (platform-wide)</div>
      <p className="text-xs text-slate-500" data-testid="ga-creds-status">
        {c.configured ? <>Configured from <b>{c.source}</b> · Client ID {c.client_id_hint}</> : "Not configured yet."} Redirect URI: <span className="mono break-all" data-testid="ga-redirect-uri">{c.redirect_uri}</span>
      </p>
      {c.can_edit && c.source !== "environment" && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
          <Input value={id} onChange={(e) => setId(e.target.value)} placeholder="Client ID" autoComplete="off" data-testid="ga-client-id-input" />
          <Input type="password" value={sec} onChange={(e) => setSec(e.target.value)} placeholder="Client secret" autoComplete="new-password" data-testid="ga-client-secret-input" />
          <Button onClick={save} disabled={busy || !id || !sec} data-testid="ga-creds-save-btn">{c.configured ? "Replace" : "Save"}</Button>
        </div>
      )}
      {c.can_edit && <p className="text-[11px] text-slate-400">Values are write-only: encrypted on the server and never shown again. Setting GOOGLE_ANALYTICS_CLIENT_ID / GOOGLE_ANALYTICS_CLIENT_SECRET as backend environment secrets overrides this.</p>}
    </div>
  );
}

function PropertyPicker({ st, onDone }) {
  const [props, setProps] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fetchProps = useCallback(() => {
    setProps(null); setLoadErr("");
    api.get("/integrations/google-analytics/properties").then((r) => setProps(r.data)).catch((e) => { setLoadErr(err(e)); setProps([]); });
  }, []);
  useEffect(() => { fetchProps(); }, [fetchProps]);
  const pick = async (pid) => {
    setBusy(true);
    try { await api.put("/integrations/google-analytics/property", { property_id: pid }); toast.success("Property saved and first sync complete"); onDone(); }
    catch (e) { toast.error(err(e)); onDone(); } finally { setBusy(false); }
  };
  if (!props) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;
  if (loadErr) return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-rose-700 bg-rose-50 rounded-xl px-3 py-2" data-testid="ga-properties-error">
      <AlertTriangle className="w-4 h-4 shrink-0" /><span className="flex-1">Could not load GA4 properties: {loadErr}</span>
      <Button size="sm" variant="outline" onClick={fetchProps} data-testid="ga-properties-retry-btn">Retry</Button>
    </div>
  );
  if (!props.length) return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-amber-700" data-testid="ga-no-properties">
      <span className="flex-1">Google returned no GA4 properties for this account. Check the account has at least Viewer access to a GA4 property.</span>
      <Button size="sm" variant="outline" onClick={fetchProps} data-testid="ga-properties-retry-btn">Retry</Button>
    </div>
  );
  return (
    <div className="flex items-center gap-2" data-testid="ga-property-picker">
      <Select value={st.property_id || ""} onValueChange={pick} disabled={busy}>
        <SelectTrigger className="w-full md:w-96" data-testid="ga-property-select"><SelectValue placeholder="Choose a GA4 property" /></SelectTrigger>
        <SelectContent>{props.map((p) => <SelectItem key={p.property_id} value={p.property_id} data-testid={`ga-property-${p.property_id}`}>{p.account_name} · {p.display_name} ({p.property_id})</SelectItem>)}</SelectContent>
      </Select>
      {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
    </div>
  );
}

const STATUS = { connected: ["Connected", "bg-emerald-100 text-emerald-700"], reauth_required: ["Reconnect needed", "bg-amber-100 text-amber-800"],
  error: ["Sync error", "bg-rose-100 text-rose-700"], not_connected: ["Not connected", "bg-slate-100 text-slate-600"] };

function GaCard() {
  const [st, setSt] = useState(null);
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState("");
  const [picking, setPicking] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const load = useCallback(() => {
    setLoadErr("");
    api.get("/integrations/google-analytics/status").then((r) => setSt(r.data)).catch((e) => setLoadErr(loadErrMsg(e)));
    api.get("/integrations/google-oauth/credentials").then((r) => setC(r.data)).catch((e) => setLoadErr(loadErrMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(err(e)); } finally { setBusy(""); load(); } };
  const connect = () => act("connect", async () => { const { data } = await api.get("/oauth/google-analytics/connect"); window.location.assign(data.authorization_url); });
  const sync = () => act("sync", async () => { await api.post("/ga4/sync-now"); toast.success("Synced"); });
  const disconnect = () => { if (window.confirm("Disconnect Google Analytics for this company? Saved reports are deleted.")) act("disc", async () => { await api.post("/integrations/google-analytics/disconnect"); toast.success("Disconnected"); }); };
  if (loadErr) return <LoadError name="Google Analytics" msg={loadErr} onRetry={load} testid="ga-load-error" />;
  if (!st || !c) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  const [label, tone] = STATUS[st.status] || STATUS.connected;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-4" data-testid="ga-integration-card">
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><BarChart3 className="w-5 h-5" /></span>
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-base font-bold text-slate-900">Google Analytics 4</h2>
          <p className="text-xs text-slate-500">Visitors, traffic sources, landing pages, conversions, purchases and revenue. Read-only access.</p>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${tone}`} data-testid="ga-status-pill">{label}</span>
      </div>
      {st.connected && (
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm" data-testid="ga-connection-details">
          <div><dt className="text-[11px] uppercase text-slate-400 font-semibold">Property</dt><dd data-testid="ga-property-name">{st.property_name ? `${st.property_name} (${st.property_id})` : "Not selected"}</dd></div>
          <div><dt className="text-[11px] uppercase text-slate-400 font-semibold">Last sync</dt><dd data-testid="ga-last-sync">{st.last_sync_at ? formatDateTime(st.last_sync_at) : "Never"}</dd></div>
          <div><dt className="text-[11px] uppercase text-slate-400 font-semibold">Connected by</dt><dd>{st.connected_by || "–"}{st.connected_at ? ` · ${formatDateTime(st.connected_at)}` : ""}</dd></div>
        </dl>
      )}
      {st.last_error && !(picking || !st.property_id) && <p className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 rounded-xl px-3 py-2" data-testid="ga-last-error"><AlertTriangle className="w-4 h-4 shrink-0" />{st.last_error}{st.last_error_at ? ` (${formatDateTime(st.last_error_at)})` : ""}</p>}
      {st.connected && st.status !== "reauth_required" && (picking || !st.property_id) && <PropertyPicker st={st} onDone={() => { setPicking(false); load(); }} />}
      <div className="flex flex-wrap gap-2">
        {(!st.connected || st.status === "reauth_required") && <Button onClick={connect} disabled={!c.configured || busy === "connect"} className="gap-1.5" data-testid="ga-connect-btn"><Plug className="w-4 h-4" />{st.connected ? "Reconnect Google Analytics" : "Connect Google Analytics"}</Button>}
        {st.connected && st.status !== "reauth_required" && st.property_id && <>
          <Button variant="outline" onClick={sync} disabled={!!busy} className="gap-1.5" data-testid="ga-sync-btn">{busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Sync now</Button>
          <Button variant="outline" onClick={() => setPicking(true)} data-testid="ga-change-property-btn">Change property</Button>
          <Button variant="outline" asChild data-testid="ga-view-report-btn"><Link to="/marketing/website-analytics"><CheckCircle2 className="w-4 h-4 mr-1.5" />View report</Link></Button>
        </>}
        {st.connected && <Button variant="outline" onClick={connect} disabled={!!busy} data-testid="ga-reconnect-btn" className={st.status === "reauth_required" ? "hidden" : ""}>Reconnect</Button>}
        {st.connected && <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={disconnect} disabled={!!busy} data-testid="ga-disconnect-btn">Disconnect</Button>}
      </div>
      <CredentialsForm c={c} onSaved={load} />
    </section>
  );
}

export default function IntegrationsPage() {
  const { can } = useAuth();
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const g = sp.get("ga");
    const a = sp.get("gads");
    const sc = sp.get("gsc");
    const m = sp.get("gmc");
    if (m) { (m === "connected" ? toast.success : toast.error)(m === "connected" ? "Google Merchant Center connected. Choose your Merchant Center account." : (GA_MSG[m] || "Google Merchant Center connection failed").replace("Analytics read access", "Merchant Center access")); setSp({}, { replace: true }); }
    if (sc) { (sc === "connected" ? toast.success : toast.error)(sc === "connected" ? "Search Console connected. Choose your websites." : (GA_MSG[sc] || "Search Console connection failed").replace("Analytics read access", "Search Console access")); setSp({}, { replace: true }); }
    if (a) { (a === "connected" ? toast.success : toast.error)(a === "connected" ? "Google Ads connected." : (GA_MSG[a] || "Google Ads connection failed").replace("Analytics read access", "Google Ads access")); setSp({}, { replace: true }); }
    if (g) { (g === "connected" ? toast.success : toast.error)(GA_MSG[g] || "Google Analytics connection failed"); setSp({}, { replace: true }); }
  }, [sp, setSp]);
  return (
    <div className="p-4 sm:p-8 max-w-5xl space-y-6" data-testid="integrations-page">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Plug className="w-5 h-5" /></span>
        <div><h1 className="text-2xl font-bold text-slate-900">Integrations</h1><p className="text-sm text-slate-500">Connect this company's own accounts. Each company's credentials and data stay separate.</p></div>
      </div>
      <SendGridCard />
      <EbayIntegrationCard />
      {can("whatsapp.settings.manage") && <WaConnectCard />}
      {can("settings.manage") && <><GaCard />
      <GoogleAdsCard />
      <SearchConsoleCard />
      <GoogleMerchantCard /></>}
    </div>
  );
}
